import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  where,
} from "firebase/firestore";
import { db } from "../../services/firebase";
import { useAuth } from "../../providers/AuthProvider";
import { hasAnyGrant } from "../../services/shared";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useNavigate } from "react-router-dom";

interface LpAgentItem {
  hash: string;
  title: string;
  org: string;
  shortLink?: string;
  createdAt: Timestamp;
}

const PAGE_SIZE = 20;

export default function LpAgents() {
  const navigate = useNavigate();
  const { grants, user } = useAuth();
  const canSeeAll = hasAnyGrant(grants, ["admin", "lp_admin"]);

  const [allItems, setAllItems] = useState<LpAgentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);

  // Reset paginazione ad ogni nuova ricerca
  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  // Listener Firestore
  useEffect(() => {
    if (!canSeeAll && !user?.org) {
      setError("Organizzazione non configurata per questo utente");
      setLoading(false);
      return;
    }

    const q = canSeeAll
      ? query(
          collection(db, "lp"),
          where("type", "==", "agent"),
          orderBy("createdAt", "desc")
        )
      : query(
          collection(db, "lp"),
          where("type", "==", "agent"),
          where("org", "==", user!.org),
          orderBy("createdAt", "desc")
        );

    const unsub = onSnapshot(
      q,
      (snap) => {
        setAllItems(
          snap.docs.map((d) => ({ hash: d.id, ...d.data() } as LpAgentItem))
        );
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setError("Errore nel caricamento delle landing page");
        setLoading(false);
      }
    );
    return () => unsub();
  }, [canSeeAll, user?.org]);

  // Ultime 5 — sempre dal dataset completo (già ordinato desc)
  const recentItems = useMemo(() => allItems.slice(0, 5), [allItems]);

  // Filtro per ricerca su title e shortLink
  const filteredItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return allItems;
    return allItems.filter(
      (item) =>
        item.title?.toLowerCase().includes(term) ||
        item.shortLink?.toLowerCase().includes(term)
    );
  }, [allItems, searchTerm]);

  // Slice per pagina corrente
  const paginatedItems = useMemo(
    () => filteredItems.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [filteredItems, page]
  );

  const copyPublicLink = (hash: string) => {
    navigator.clipboard.writeText(
      `${window.location.origin}/lp-agente/${hash}`
    );
  };

  const formatDate = (ts: Timestamp) =>
    ts?.toDate().toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

  if (loading)
    return (
      <div className="container">
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      </div>
    );

  return (
    <div className="container">
      {/* ── Header ── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Landing Page Agenti
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/lp-agents/create")}
        >
          Nuova LP
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* ── Widget ultime 5 ── */}
      {recentItems.length > 0 && !searchTerm && (
        <>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Ultime landing page
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              overflowX: "auto",
              pb: 1,
              mb: 3,
              "&::-webkit-scrollbar": { height: 4 },
              "&::-webkit-scrollbar-thumb": {
                background: "#e0e0e0",
                borderRadius: 2,
              },
            }}
          >
            {recentItems.map((item) => (
              <Card
                key={item.hash}
                variant="outlined"
                sx={{ minWidth: 200, maxWidth: 220, flexShrink: 0 }}
              >
                <CardActionArea
                  onClick={() => navigate(`/lp-agents/${item.hash}`)}
                  sx={{ p: 1.5 }}
                >
                  <CardContent sx={{ p: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      noWrap
                      title={item.title}
                    >
                      {item.title}
                    </Typography>
                    {item.shortLink ? (
                      <Chip
                        label={item.shortLink}
                        size="small"
                        sx={{ mt: 0.5, fontSize: 11 }}
                      />
                    ) : (
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        nessun shortlink
                      </Typography>
                    )}
                    {item.createdAt && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.5 }}
                      >
                        {formatDate(item.createdAt)}
                      </Typography>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
          <Divider sx={{ mb: 3 }} />
        </>
      )}

      {/* ── Barra di ricerca ── */}
      <TextField
        placeholder="Cerca per titolo o shortlink…"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        size="small"
        fullWidth
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: searchTerm ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearchTerm("")}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />

      {/* ── Contatore risultati ── */}
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
        {searchTerm
          ? `${filteredItems.length} risultat${filteredItems.length === 1 ? "o" : "i"} su ${allItems.length}`
          : `${allItems.length} landing page tot${allItems.length === 1 ? "ale" : "ali"}`}
      </Typography>

      {/* ── Tabella principale ── */}
      {filteredItems.length === 0 ? (
        <Typography color="text.secondary" sx={{ mt: 2 }}>
          {searchTerm
            ? "Nessuna landing page corrisponde alla ricerca."
            : "Nessuna landing page agente trovata."}
        </Typography>
      ) : (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Titolo</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>ShortLink</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Org</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Creata il</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Azioni
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedItems.map((item) => (
                  <TableRow
                    key={item.hash}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/lp-agents/${item.hash}`)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 220 }}>
                        {item.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {item.shortLink ? (
                        <Chip
                          label={item.shortLink}
                          size="small"
                          sx={{ fontFamily: "monospace", fontSize: 12 }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 140 }}>
                        {item.org}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {item.createdAt ? formatDate(item.createdAt) : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Tooltip title="Copia link pubblico">
                        <IconButton
                          size="small"
                          onClick={() => copyPublicLink(item.hash)}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Apri landing page">
                        <IconButton
                          size="small"
                          onClick={() =>
                            window.open(
                              `/lp-agente/${item.hash}`,
                              "_blank",
                              "noopener"
                            )
                          }
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredItems.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} di ${count}`
            }
          />
        </Paper>
      )}
    </div>
  );
}
